import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Navigation } from "../components/Navigation";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Euro, Heart, Package, Share2, ShoppingCart, Star, ArrowLeft, ZoomIn, Eye } from "lucide-react";
import { Lightbox } from "../components/prodotti/Lightbox";
import { ReviewSection } from "../components/prodotti/ReviewSection";
import { ProductCard, promoTimeLeft } from "../components/prodotti/ProductCard";
import { useProductViews, formatViews } from "../hooks/useProductViews";
import { isSoldOut, SoldOutStamp, SOLD_OUT_IMG } from "../components/prodotti/SoldOutStamp";
import { PreorderCountdown } from "../components/prodotti/PreorderCountdown";
import { ShareDialog } from "../components/ShareDialog";
import { formatPrice, formatPriceWithIva, findArticleDiscount, getDiscountedPrice } from "../components/prodotti/pricing";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const KlarnaBadge = ({ article, discount }) => {
  // Compute final price including IVA + any active discount
  const ivaMult = 1 + (article.iva_percentage || 0) / 100;
  const base = article.price * ivaMult;
  const finalPrice = discount ? getDiscountedPrice(article.price, article.iva_percentage, discount) : base;
  // Klarna "Pay in 3" availability: min ~10€ (Stripe gates at checkout), max 3000€
  if (finalPrice < 10 || finalPrice > 3000) return null;
  const installment = finalPrice / 3;
  return (
    <div
      className="mt-3 flex items-center gap-2 bg-[#FFE5EE] border border-[#FFA8C5] rounded-lg px-3 py-2"
      data-testid="klarna-installment-badge"
    >
      <span className="bg-black text-[#FFA8C5] font-extrabold text-[11px] px-2 py-0.5 rounded-full leading-none">
        Klarna.
      </span>
      <span className="text-sm text-gray-800">
        oppure 3 rate da <strong>€{installment.toFixed(2)}</strong> · 0% interessi
      </span>
    </div>
  );
};

const PayPalPayLaterBadge = ({ article, discount }) => {
  // Compute final price including IVA + any active discount
  const ivaMult = 1 + (article.iva_percentage || 0) / 100;
  const base = article.price * ivaMult;
  const finalPrice = discount ? getDiscountedPrice(article.price, article.iva_percentage, discount) : base;
  // PayPal "Paga in 3" Italy availability: €30 – €2000, 3 monthly installments, 0% interest
  if (finalPrice < 30 || finalPrice > 2000) return null;
  const installment = finalPrice / 3;
  return (
    <div
      className="mt-2 flex items-center gap-2 bg-[#EAF3FF] border border-[#0070BA] rounded-lg px-3 py-2"
      data-testid="paypal-paylater-badge"
    >
      <span className="font-extrabold text-[11px] px-2 py-0.5 rounded-full leading-none border border-[#0070BA] bg-white">
        <span className="text-[#003087]">Pay</span>
        <span className="text-[#0070BA]">Pal</span>
      </span>
      <span className="text-sm text-gray-800">
        oppure 3 rate da <strong>€{installment.toFixed(2)}</strong> · 0% interessi
      </span>
    </div>
  );
};

/**
 * KlarnaCTABanner — high-conversion callout placed right above "Aggiungi al Carrello".
 * More prominent than the info-style KlarnaBadge: full-width, pink Klarna branding,
 * action-oriented copy targeting collectors spending €30-€100. Shown only in the
 * Klarna installments range (€10-€3000).
 */
const KlarnaCTABanner = ({ article, discount }) => {
  const ivaMult = 1 + (article.iva_percentage || 0) / 100;
  const base = article.price * ivaMult;
  const finalPrice = discount ? getDiscountedPrice(article.price, article.iva_percentage, discount) : base;
  if (finalPrice < 10 || finalPrice > 3000) return null;
  const installment = finalPrice / 3;
  return (
    <div
      className="relative overflow-hidden rounded-xl bg-gradient-to-r from-[#FFE5EE] via-[#FFCFDF] to-[#FFE5EE] border-2 border-[#FFA8C5] px-4 py-3.5 shadow-sm"
      data-testid="klarna-cta-banner"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="bg-black text-[#FFA8C5] font-extrabold text-[11px] px-2 py-0.5 rounded-full leading-none">
              Klarna.
            </span>
            <span className="text-[11px] font-semibold text-[#B4185A] uppercase tracking-wide">
              Rateizza senza interessi
            </span>
          </div>
          <div className="text-[15px] sm:text-base text-black leading-snug">
            <strong>3 rate da €{installment.toFixed(2)}</strong>
            <span className="text-black"> · 0% interessi · Scelto al checkout</span>
          </div>
        </div>
        <div className="flex-shrink-0 hidden sm:flex items-center justify-center w-12 h-12 rounded-full bg-white border-2 border-[#FFA8C5]">
          <span className="text-xl">💳</span>
        </div>
      </div>
    </div>
  );
};

const PromoCountdown = ({ discount }) => {
  const [left, setLeft] = useState(() => promoTimeLeft(discount?.expiry_date));
  useEffect(() => {
    setLeft(promoTimeLeft(discount?.expiry_date));
    const t = setInterval(() => setLeft(promoTimeLeft(discount?.expiry_date)), 60000);
    return () => clearInterval(t);
  }, [discount?.expiry_date]);
  if (!left) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div
        className={`inline-flex items-center gap-1.5 text-xs font-extrabold rounded-full px-3 py-1 border ${left.urgent ? "bg-yellow-400/15 border-yellow-400/50 text-yellow-300 animate-pulse" : "bg-red-500/10 border-red-500/40 text-red-300"}`}
        data-testid="product-promo-countdown"
      >
        ⏰ Offerta scade tra {left.label}
      </div>
      {discount?.code && (
        <span className="text-xs text-gray-300" data-testid="product-promo-code">
          Usa il codice <code className="bg-pink-500/15 text-pink-300 font-extrabold px-1.5 py-0.5 rounded">{discount.code}</code> nel carrello
        </span>
      )}
    </div>
  );
};

const PriceBlock = ({ article, discount }) => {
  if (discount) {
    const discountedPrice = getDiscountedPrice(article.price, article.iva_percentage, discount);
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <span className="text-2xl text-gray-400 line-through">
            {formatPriceWithIva(article.price, article.iva_percentage)}
          </span>
          <span className="text-4xl font-bold text-red-500" data-testid="product-discounted-price">{formatPrice(discountedPrice)}</span>
          <Badge className="bg-red-500 text-white">
            -{discount.discount_type === "percentage" ? `${discount.discount_value}%` : `€${discount.discount_value}`}
          </Badge>
        </div>
        <PromoCountdown discount={discount} />
      </div>
    );
  }
  return (
    <span className="text-4xl font-bold text-green-400">
      {formatPriceWithIva(article.price, article.iva_percentage)}
    </span>
  );
};

const AvailabilityBadge = ({ article }) => {
  if (article.availability_type === "preorder") {
    return <Badge className="bg-orange-100 text-orange-800 border-orange-200 text-base px-3 py-1">🚀 Prevendita</Badge>;
  }
  if (article.availability_type === "reservation") {
    return <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-base px-3 py-1">📋 Prenotazione</Badge>;
  }
  if (article.quantity_stock > 0) {
    return (
      <Badge className="bg-green-100 text-green-800 border-green-200 text-base px-3 py-1">
        ✅ Disponibile ({article.quantity_stock} pz)
      </Badge>
    );
  }
  return <Badge variant="destructive" className="text-base px-3 py-1">❌ Esaurito</Badge>;
};

const ImageGallery = ({ images, title, onZoom, soldOut = false, articleId = "" }) => {
  const [active, setActive] = useState(0);
  if (!images || images.length === 0) {
    return (
      <div className="aspect-[3/4] w-full bg-[#12121a] rounded-lg flex items-center justify-center">
        <Package className="w-24 h-24 text-gray-300" />
      </div>
    );
  }
  const current = images[active];
  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={() => onZoom({ src: current, alt: title })}
        className="relative aspect-[3/4] w-full overflow-hidden rounded-lg bg-white border border-gray-800 group cursor-zoom-in"
        data-testid="product-detail-main-image"
      >
        <img src={current} alt={title} className={`w-full h-full object-contain p-2 group-hover:scale-105 transition-transform ${soldOut ? SOLD_OUT_IMG : ""}`} />
        {soldOut && <SoldOutStamp articleId={articleId} size="lg" />}
        <span className="absolute top-3 right-3 bg-black/60 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
          <ZoomIn className="w-5 h-5" />
        </span>
      </button>
      {images.length > 1 && (
        <div className="grid grid-cols-5 gap-2">
          {images.map((src, i) => (
            <button
              key={src}
              type="button"
              onClick={() => setActive(i)}
              className={`aspect-square rounded border overflow-hidden ${i === active ? "border-blue-500 ring-2 ring-blue-300" : "border-gray-200"}`}
              data-testid={`product-detail-thumb-${i}`}
            >
              <img src={src} alt={`${title} ${i + 1}`} className="w-full h-full object-contain p-1" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyStatus, setNotifyStatus] = useState(null);
  const [activePromotions, setActivePromotions] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [reviews, setReviews] = useState({});
  const viewsMap = useProductViews();
  const [showReviewForm, setShowReviewForm] = useState({});
  const [reviewForm, setReviewForm] = useState({ customer_name: "", customer_email: "", rating: 5, comment: "" });
  const [wishlistIds, setWishlistIds] = useState([]);
  const [toast, setToast] = useState(null);
  const [related, setRelated] = useState([]);
  const [seriesData, setSeriesData] = useState(null);
  const [fbtItems, setFbtItems] = useState([]);
  const [fbtBasedOn, setFbtBasedOn] = useState(null);
  const [collectionPick, setCollectionPick] = useState({});  // {id: true}
  const [collectionAdding, setCollectionAdding] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [cartCount, setCartCount] = useState(() => {
    try {
      const cart = JSON.parse(localStorage.getItem("cart") || "[]");
      return cart.reduce((sum, it) => sum + (it.quantity || 0), 0);
    } catch { return 0; }
  });

  // Keep the floating cart badge in sync (own tab + cross-tab updates)
  useEffect(() => {
    const recompute = () => {
      try {
        const cart = JSON.parse(localStorage.getItem("cart") || "[]");
        setCartCount(cart.reduce((sum, it) => sum + (it.quantity || 0), 0));
      } catch { /* ignore */ }
    };
    window.addEventListener("storage", recompute);
    window.addEventListener("cart:updated", recompute);
    return () => {
      window.removeEventListener("storage", recompute);
      window.removeEventListener("cart:updated", recompute);
    };
  }, []);

  // Capture incoming referral code (?ref=ABCD12) from social-share links and
  // stash it in localStorage so the checkout/registration flow can credit
  // the referrer even if the visitor browses other products first.
  // Expires after 30 days to mimic typical affiliate cookie lifetime.
  useEffect(() => {
    try {
      const ref = new URLSearchParams(window.location.search).get("ref");
      if (ref && /^[A-Za-z0-9]{2,32}$/.test(ref)) {
        localStorage.setItem("referrer_code", ref.toUpperCase());
        localStorage.setItem("referrer_code_expires", String(Date.now() + 30 * 24 * 60 * 60 * 1000));
      }
    } catch (_) { /* ignore */ }
  }, []);

  // Load article + promotions + reviews. The URL param `id` may be either a UUID
  // or an SEO slug; the backend resolves both. We then use `article.id` (UUID)
  // for the follow-up calls (view tracking, reviews) which only accept UUIDs.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const [aRes, pRes] = await Promise.all([
          axios.get(`${API}/public/articles/${id}`),
          axios.get(`${API}/active-promotions`).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const articleData = aRes.data;
        const articleUuid = articleData.id;

        // SEO redirect: if the user landed on /prodotto/{uuid} but the article
        // has a slug, replace history with the slug URL. The canonical link tag
        // already points to the slug → Google treats this as the strong signal
        // for the new URL. `replace: true` avoids polluting browser history.
        const isUuid =
          id.length === 36 && id[8] === "-" && id[13] === "-" && id[18] === "-" && id[23] === "-";
        if (isUuid && articleData.slug && articleData.slug !== id) {
          navigate(`/prodotto/${articleData.slug}`, { replace: true });
          // continue loading — the new URL renders the same component instance
        }

        setArticle(articleData);
        setActivePromotions(pRes.data || []);
        // Track view (uses real UUID, never the slug)
        axios.post(`${API}/articles/${articleUuid}/view`).catch(() => {});
        // Load reviews
        const rRes = await axios.get(`${API}/reviews`, { params: { article_id: articleUuid, status: "approved" } }).catch(() => ({ data: { reviews: [] } }));
        if (!cancelled) setReviews({ [articleUuid]: rRes.data?.reviews || [] });
        // Wishlist for logged-in customers — the HttpOnly cookie (P5) is sent
        // automatically via axios.defaults.withCredentials=true, no need to read
        // a token from localStorage. 401s mean "not logged in" → skip silently.
        axios
          .get(`${API}/customer/wishlist`)
          .then((res) => {
            if (!cancelled) setWishlistIds((res.data || []).map((w) => w.article_id));
          })
          .catch(() => {});
        // Related products: same category, exclude current article, max 4
        if (articleData?.category) {
          axios
            .get(`${API}/public/articles`)
            .then((res) => {
              if (cancelled) return;
              const list = (res.data || [])
                .filter((a) => a.category === articleData.category && a.id !== articleUuid && a.is_active !== false)
                .slice(0, 4);
              setRelated(list);
            })
            .catch(() => {});

          // Continua la Collezione: fetch other items of the same product series.
          axios
            .get(`${API}/public/articles/${articleData.slug || articleData.id}/series`)
            .then((res) => {
              if (cancelled) return;
              if (res.data?.series && (res.data?.items || []).length > 0) {
                setSeriesData(res.data);
              }
            })
            .catch(() => {});

          // "Chi ha comprato questo, ha comprato anche...": real co-purchase
          // signal from `orders` collection (falls back to top sellers in the
          // same category for fresh products without sales history).
          axios
            .get(`${API}/public/articles/${articleData.slug || articleData.id}/frequently-bought-together`)
            .then((res) => {
              if (cancelled) return;
              setFbtItems(res.data?.items || []);
              setFbtBasedOn(res.data?.based_on || null);
            })
            .catch(() => {});
        }
      } catch (e) {
        if (cancelled) return;
        if (e.response?.status === 404) {
          setError("not_found");
        } else {
          // Transient failure (network/5xx/429): retry once before showing the
          // error UI, so Googlebot doesn't render a broken page for a live product
          try {
            await new Promise((r) => setTimeout(r, 1500));
            const retry = await axios.get(`${API}/public/articles/${id}`);
            if (!cancelled) setArticle(retry.data);
          } catch (e2) {
            if (!cancelled) setError(e2.response?.status === 404 ? "not_found" : "error");
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Set page title dynamically (React 19 hoists <title>/<link>/<meta> in JSX)
  useEffect(() => {
    if (article) {
      document.title = `${article.title} | Edicola Cerasuolo`;
    }
  }, [article]);

  const discount = useMemo(() => (article ? findArticleDiscount(activePromotions, article.id) : null), [activePromotions, article]);

  const images = useMemo(() => {
    if (!article) return [];
    const list = [];
    if (article.images_url && article.images_url.length > 0) list.push(...article.images_url);
    else if (article.photo_url) list.push(`${BACKEND_URL}${article.photo_url}`);
    return list;
  }, [article]);

  // Memoize SEO strings to avoid recomputing/rerendering meta tags every render
  const metaDescription = useMemo(() => {
    if (!article) return "";
    return (article.description || article.title || "").slice(0, 160).replace(/\s+/g, " ").trim();
  }, [article]);

  const ogDescription = useMemo(() => {
    if (!article) return "";
    return (article.description || article.title || "").slice(0, 200).replace(/\s+/g, " ").trim();
  }, [article]);

  const productJsonLd = useMemo(() => {
    if (!article) return "";
    const finalPrice = (article.price * (1 + (article.iva_percentage || 0) / 100)).toFixed(2);
    const productUrl = `https://edicolavillaricca.com/prodotto/${article.slug || article.id}`;
    const reviewList = (reviews[article.id] || []).filter((r) => r && r.rating);
    const ratingValue = reviewList.length
      ? (reviewList.reduce((s, r) => s + Number(r.rating || 0), 0) / reviewList.length).toFixed(1)
      : null;
    // Google requires priceValidUntil for merchant listings — set 30 days ahead
    const validUntil = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

    const schema = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: article.title,
      description: article.description || article.title,
      sku: article.sku || article.id,
      category: article.category,
      image: images,
      brand: {
        "@type": "Brand",
        name: article.brand || article.category || "Edicola Cerasuolo",
      },
      offers: {
        "@type": "Offer",
        priceCurrency: "EUR",
        price: finalPrice,
        priceValidUntil: validUntil,
        itemCondition: article.ebay_condition === "USED"
          ? "https://schema.org/UsedCondition"
          : "https://schema.org/NewCondition",
        availability:
          article.quantity_stock > 0
            ? "https://schema.org/InStock"
            : article.availability_type === "preorder"
              ? "https://schema.org/PreOrder"
              : article.availability_type === "reservation"
                ? "https://schema.org/PreOrder"
                : "https://schema.org/OutOfStock",
        url: productUrl,
        seller: {
          "@type": "Organization",
          name: "Edicola Cerasuolo",
        },
        hasMerchantReturnPolicy: {
          "@type": "MerchantReturnPolicy",
          applicableCountry: "IT",
          returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
          merchantReturnDays: 14,
          returnMethod: "https://schema.org/ReturnByMail",
          returnFees: "https://schema.org/FreeReturn",
        },
        shippingDetails: {
          "@type": "OfferShippingDetails",
          shippingRate: {
            "@type": "MonetaryAmount",
            value: "6.90",
            currency: "EUR",
          },
          shippingDestination: {
            "@type": "DefinedRegion",
            addressCountry: "IT",
          },
          deliveryTime: {
            "@type": "ShippingDeliveryTime",
            handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 2, unitCode: "DAY" },
            transitTime: { "@type": "QuantitativeValue", minValue: 1, maxValue: 3, unitCode: "DAY" },
          },
        },
      },
    };

    // Only add EAN if present (Google requires valid GTIN-13 or omit)
    if (article.ean_code && /^\d{8,14}$/.test(String(article.ean_code).trim())) {
      schema.gtin13 = String(article.ean_code).trim();
    }
    // MPN (Manufacturer Part Number) from SKU
    if (article.sku) {
      schema.mpn = article.sku;
    }
    // Aggregate rating (only if we have reviews — Google penalizes fake ratings)
    if (reviewList.length > 0 && ratingValue) {
      schema.aggregateRating = {
        "@type": "AggregateRating",
        ratingValue,
        reviewCount: reviewList.length,
        bestRating: "5",
        worstRating: "1",
      };
      schema.review = reviewList.slice(0, 5).map((r) => ({
        "@type": "Review",
        reviewRating: {
          "@type": "Rating",
          ratingValue: String(r.rating),
          bestRating: "5",
        },
        author: {
          "@type": "Person",
          name: r.customer_name || r.author || "Cliente Verificato",
        },
        reviewBody: r.comment || r.text || "",
        datePublished: (r.created_at || r.date || "").slice(0, 10) || undefined,
      }));
    }
    return JSON.stringify(schema);
  }, [article, images, reviews]);

  const showToast = (msg, kind = "success") => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 3000);
  };

  const addToCart = () => {
    const cart = JSON.parse(localStorage.getItem("cart") || "[]");
    const idx = cart.findIndex((i) => i.article_id === article.id);
    if (idx >= 0) cart[idx].quantity += 1;
    else cart.push({ article_id: article.id, quantity: 1, article });
    localStorage.setItem("cart", JSON.stringify(cart));
    setCartCount(cart.reduce((sum, it) => sum + (it.quantity || 0), 0));
    window.dispatchEvent(new Event("cart:updated"));
    showToast("🛒 Aggiunto al carrello!");
  };

  // Quick-add per i suggerimenti cross-sell (FBT / correlati)
  const quickAddToCart = (a) => {
    const cart = JSON.parse(localStorage.getItem("cart") || "[]");
    const idx = cart.findIndex((i) => i.article_id === a.id);
    if (idx >= 0) cart[idx].quantity += 1;
    else cart.push({ article_id: a.id, quantity: 1, article: a });
    localStorage.setItem("cart", JSON.stringify(cart));
    setCartCount(cart.reduce((sum, it) => sum + (it.quantity || 0), 0));
    window.dispatchEvent(new Event("cart:updated"));
    showToast(`🛒 ${a.title} aggiunto al carrello!`);
  };

  const addCollectionToCart = () => {
    const selectedIds = Object.keys(collectionPick).filter((id) => collectionPick[id]);
    if (selectedIds.length === 0) {
      showToast("Seleziona almeno un numero della serie", "error");
      return;
    }
    setCollectionAdding(true);
    try {
      const cart = JSON.parse(localStorage.getItem("cart") || "[]");
      const itemsById = new Map((seriesData?.items || []).map((it) => [it.id, it]));
      let added = 0;
      for (const id of selectedIds) {
        const sib = itemsById.get(id);
        if (!sib) continue;
        const idx = cart.findIndex((i) => i.article_id === id);
        if (idx >= 0) {
          cart[idx].quantity += 1;
        } else {
          cart.push({ article_id: id, quantity: 1, article: sib });
        }
        added += 1;
      }
      localStorage.setItem("cart", JSON.stringify(cart));
      setCartCount(cart.reduce((sum, it) => sum + (it.quantity || 0), 0));
      window.dispatchEvent(new Event("cart:updated"));
      setCollectionPick({});
      showToast(`🛒 Aggiunti ${added} pezzi della collezione al carrello!`);
    } finally {
      setCollectionAdding(false);
    }
  };

  const toggleWishlist = async () => {
    // No localStorage lookup: the HttpOnly cookie (P5) is sent automatically.
    // On 401 we know the user isn't logged in and prompt for login.
    try {
      if (wishlistIds.includes(article.id)) {
        await axios.delete(`${API}/customer/wishlist/${article.id}`);
        setWishlistIds((prev) => prev.filter((x) => x !== article.id));
        showToast("Rimosso dai preferiti");
      } else {
        await axios.post(`${API}/customer/wishlist`, { article_id: article.id });
        setWishlistIds((prev) => [...prev, article.id]);
        showToast("❤️ Aggiunto ai preferiti!");
      }
    } catch (e) {
      if (e.response?.status === 401) {
        showToast("Accedi per usare la wishlist", "error");
        navigate("/login?next=" + encodeURIComponent(window.location.pathname));
      } else {
        showToast("Errore wishlist", "error");
      }
    }
  };

  const submitReview = async (articleId) => {
    if (!reviewForm.customer_name || !reviewForm.customer_email || !reviewForm.comment) {
      alert("Compila tutti i campi della recensione");
      return;
    }
    try {
      await axios.post(`${API}/reviews`, { ...reviewForm, article_id: articleId });
      const rRes = await axios.get(`${API}/reviews`, { params: { article_id: articleId, status: "approved" } });
      setReviews({ [articleId]: rRes.data?.reviews || [] });
      setReviewForm({ customer_name: "", customer_email: "", rating: 5, comment: "" });
      setShowReviewForm({ [articleId]: false });
      showToast("⭐ Grazie! La recensione sarà visibile dopo l'approvazione.");
    } catch (e) {
      showToast("Errore invio recensione", "error");
    }
  };

  const shareProduct = () => setShareOpen(true);

  const subscribeStockAlert = async () => {
    const email = notifyEmail.trim();
    if (!email || !email.includes("@")) {
      setNotifyStatus("error");
      return;
    }
    setNotifyStatus("sending");
    try {
      await axios.post(`${API}/public/articles/${article.slug || article.id}/notify-me`, { email });
      setNotifyStatus("done");
    } catch (e) {
      setNotifyStatus("error");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f]">
        <Navigation />
        <div className="flex items-center justify-center py-32">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-400">Caricamento prodotto...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error === "error" && !article) {
    // Transient API error: NO noindex here — the product may be perfectly live.
    // Googlebot sees a retryable error page and keeps the URL indexed.
    return (
      <div className="min-h-screen bg-[#0a0a0f]">
        <title>Errore temporaneo — Edicola Cerasuolo</title>
        <Navigation />
        <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="product-transient-error">
          <Package className="w-24 h-24 text-gray-300 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white mb-2">Errore temporaneo</h1>
          <p className="text-gray-400 mb-6">Non siamo riusciti a caricare il prodotto. Riprova tra qualche istante.</p>
          <Button onClick={() => window.location.reload()} className="bg-blue-600 hover:bg-blue-700">
            🔄 Riprova
          </Button>
        </div>
      </div>
    );
  }

  if (error === "not_found" || !article) {
    return (
      <div className="min-h-screen bg-[#0a0a0f]">
        {/* SEO: tell Googlebot this page is not real content so it stops being
            counted as Soft 404. Applies via React 19 native <head> hoisting. */}
        <title>Prodotto non trovato — Edicola Cerasuolo</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
        <meta httpEquiv="Status" content="404 Not Found" />
        <Navigation />
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <Package className="w-24 h-24 text-gray-300 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white mb-2">Prodotto non trovato</h1>
          <p className="text-gray-400 mb-6">Il prodotto che stai cercando non esiste più o è stato rimosso.</p>
          <Button onClick={() => navigate("/prodotti")} className="bg-blue-600 hover:bg-blue-700">
            <ArrowLeft className="w-4 h-4 mr-2" /> Torna al catalogo
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      {/* React 19 native head tags — dynamic per product (hoisted to <head>) */}
      <title>{`${article.title} | Edicola Cerasuolo`}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={`https://edicolavillaricca.com/prodotto/${article.slug || article.id}`} />
      <meta property="og:title" content={article.title} />
      <meta property="og:type" content="product" />
      <meta property="og:url" content={`https://edicolavillaricca.com/prodotto/${article.slug || article.id}`} />
      {images[0] && <meta property="og:image" content={images[0]} />}
      <meta property="og:description" content={ogDescription} />
      {/* Schema.org Product — rich snippet on Google */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: productJsonLd }} />

      <Navigation />

      {/* Toast */}
      {toast && (
        <div className={`fixed top-20 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white ${toast.kind === "error" ? "bg-red-600" : "bg-green-600"}`}>
          {toast.msg}
        </div>
      )}

      {/* Floating Cart Button — opens the cart on /prodotti */}
      {cartCount > 0 && (
        <div className="fixed top-24 right-4 z-50 md:top-28 md:right-6">
          <Button
            onClick={() => navigate("/prodotti?openCart=1")}
            className="bg-green-600 hover:bg-green-700 rounded-full px-5 py-6 shadow-2xl text-base font-bold animate-pulse"
            data-testid="cart-toggle-detail"
            style={{ animation: "cartPulse 2s ease-in-out infinite" }}
          >
            <ShoppingCart className="w-5 h-5 mr-2" />
            Carrello ({cartCount})
          </Button>
          <style>{`
            @keyframes cartPulse {
              0%, 100% { box-shadow: 0 10px 25px -5px rgba(34, 197, 94, 0.6); transform: scale(1); }
              50% { box-shadow: 0 16px 36px -3px rgba(34, 197, 94, 0.9); transform: scale(1.05); }
            }
          `}</style>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="text-sm text-gray-500 mb-6" data-testid="product-detail-breadcrumb">
          <Link to="/" className="hover:text-pink-400">Home</Link>
          <span className="mx-2">›</span>
          <Link to="/prodotti" className="hover:text-pink-400">Prodotti</Link>
          {article.category && (
            <>
              <span className="mx-2">›</span>
              <span className="text-gray-500">{article.category}</span>
            </>
          )}
          <span className="mx-2">›</span>
          <span className="text-gray-200 font-medium">{article.title}</span>
        </nav>

        <Button variant="outline" onClick={() => navigate(-1)} className="mb-4 bg-transparent border-gray-700 text-gray-300 hover:bg-[#16161f] hover:text-white" data-testid="product-detail-back">
          <ArrowLeft className="w-4 h-4 mr-2" /> Indietro
        </Button>

        {/* Main grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Left: gallery */}
          <ImageGallery images={images} title={article.title} onZoom={setLightbox} soldOut={isSoldOut(article)} articleId={article.id} />

          {/* Right: product info */}
          <div className="space-y-4">
            {article.category && (
              <Badge variant="secondary" className="text-base bg-[#1c1c26] text-gray-300 border border-gray-700">{article.category}</Badge>
            )}

            <h1 className="text-3xl lg:text-4xl font-bold text-white" data-testid="product-detail-title">
              {article.title}
            </h1>

            {viewsMap[article.id] > 0 && (
              <span className="inline-flex items-center gap-1.5 text-sm text-gray-400 mr-4" title="Visualizzazioni della pagina prodotto" data-testid="product-detail-views">
                <Eye className="w-4 h-4" /> {formatViews(viewsMap[article.id])} visualizzazioni
              </span>
            )}
            {(() => {
              const list = (reviews[article.id] || []).filter((r) => r && r.rating);
              if (!list.length) return null;
              const avg = list.reduce((s, r) => s + Number(r.rating), 0) / list.length;
              return (
                <a href="#recensioni-prodotto" className="inline-flex items-center gap-2 text-sm group" data-testid="product-rating-summary">
                  <span className="flex">{[1, 2, 3, 4, 5].map((s) => (<Star key={s} className={`w-4 h-4 ${s <= Math.round(avg) ? "text-yellow-400 fill-yellow-400" : "text-gray-600"}`} />))}</span>
                  <span className="font-bold text-white">{avg.toFixed(1)}</span>
                  <span className="text-gray-400 group-hover:text-pink-300 underline-offset-2 group-hover:underline">{list.length} {list.length === 1 ? "recensione" : "recensioni"}</span>
                </a>
              );
            })()}

            <div className="flex items-center gap-3 flex-wrap">
              <AvailabilityBadge article={article} />
              {article.ebay?.listing_id && (
                <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-base px-3 py-1">
                  🛒 Anche su eBay
                </Badge>
              )}
            </div>

            <div className="bg-[#12121a] rounded-lg p-5 border border-gray-800">
              <PriceBlock article={article} discount={discount} />
              <p className="text-sm text-gray-500 mt-2">
                Prezzo base: {formatPrice(article.price)} + IVA {article.iva_percentage}%
              </p>
              <KlarnaBadge article={article} discount={discount} />
              {/* PayPalPayLaterBadge nascosto temporaneamente: account PayPal Business in attesa
                  di verifica (errore PAYEE_ACCOUNT_RESTRICTED). Riattiva rimuovendo il commento. */}
              {/* <PayPalPayLaterBadge article={article} discount={discount} /> */}
            </div>

            {article.availability_type === "preorder" && article.preorder_date && (
              <PreorderCountdown preorderDate={article.preorder_date} variant="light" />
            )}
            {article.availability_type === "reservation" && article.reservation_info && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-blue-800 font-medium">ℹ️ {article.reservation_info}</p>
              </div>
            )}

            {/* Action buttons */}
            <KlarnaCTABanner article={article} discount={discount} />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {article.quantity_stock > 0 ? (
                <Button
                  onClick={addToCart}
                  className="sm:col-span-2 bg-green-600 hover:bg-green-700 text-lg py-6"
                  data-testid="product-detail-add-to-cart"
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  Aggiungi al Carrello
                </Button>
              ) : (
                <div className="sm:col-span-2 space-y-2" data-testid="back-soon-box">
                  <div className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-3">
                    <p className="text-amber-900 font-semibold">😔 Attualmente esaurito</p>
                    <p className="text-amber-800 text-sm">Torna presto disponibile — lasciaci la tua email e ti avvisiamo appena rientra.</p>
                  </div>
                  {notifyStatus === "done" ? (
                    <div className="bg-green-50 border border-green-300 rounded-lg px-4 py-3 text-green-800 text-sm font-medium" data-testid="notify-me-success">
                      ✅ Perfetto! Ti avviseremo appena torna disponibile.
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        type="email"
                        placeholder="La tua email"
                        value={notifyEmail}
                        onChange={(e) => setNotifyEmail(e.target.value)}
                        className="flex-1"
                        data-testid="notify-me-email-input"
                      />
                      <Button
                        onClick={subscribeStockAlert}
                        disabled={notifyStatus === "sending"}
                        className="bg-amber-600 hover:bg-amber-700 whitespace-nowrap"
                        data-testid="notify-me-submit"
                      >
                        {notifyStatus === "sending" ? "…" : "🔔 Avvisami"}
                      </Button>
                    </div>
                  )}
                  {notifyStatus === "error" && (
                    <p className="text-red-600 text-xs" data-testid="notify-me-error">Email non valida, ricontrolla.</p>
                  )}
                </div>
              )}
              <Button
                variant="outline"
                onClick={toggleWishlist}
                className={wishlistIds.includes(article.id) ? "bg-pink-500/10 border-pink-500/50 text-pink-400 hover:bg-pink-500/20" : "bg-transparent border-gray-700 text-gray-300 hover:bg-[#16161f] hover:text-pink-400"}
                data-testid="product-detail-wishlist"
              >
                <Heart className={`w-5 h-5 mr-2 ${wishlistIds.includes(article.id) ? "fill-current" : ""}`} />
                {wishlistIds.includes(article.id) ? "Nei preferiti" : "Preferiti"}
              </Button>
            </div>

            {/* Condividi prodotto */}
            <div className="flex flex-wrap items-center gap-2" data-testid="product-share">
              <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold mr-1">Condividi:</span>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`Guarda questo su Edicola Cerasuolo: ${article.title} — https://edicolavillaricca.com/prodotto/${article.slug || article.id}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#25D366] hover:bg-[#1fb857] text-black font-bold text-xs rounded-full transition-colors"
                data-testid="product-share-whatsapp"
              >
                💬 WhatsApp
              </a>
              <button
                type="button"
                onClick={() => {
                  const url = `https://edicolavillaricca.com/prodotto/${article.slug || article.id}`;
                  navigator.clipboard?.writeText(url).then(
                    () => showToast("Link copiato! 🔗"),
                    () => showToast("Impossibile copiare il link", "error")
                  );
                }}
                className="inline-flex items-center gap-1.5 px-4 py-2 border border-gray-700 hover:border-pink-400 hover:text-pink-400 text-gray-400 font-bold text-xs rounded-full transition-colors"
                data-testid="product-share-copy"
              >
                🔗 Copia link
              </button>
            </div>

            <Button variant="outline" onClick={shareProduct} className="w-full border-gray-700 bg-transparent text-gray-200 hover:bg-gray-800 hover:text-white" data-testid="product-detail-share">
              <Share2 className="w-4 h-4 mr-2" /> Condividi prodotto
            </Button>

            <Card className="bg-[#12121a] border border-gray-800">
              <CardContent className="pt-5 space-y-2 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-gray-400">SKU:</span>
                  <span className="font-mono text-white">{article.sku}</span>
                  {article.weight_grams && (
                    <>
                      <span className="text-gray-400">Peso:</span>
                      <span className="text-white">{article.weight_grams}g</span>
                    </>
                  )}
                  {article.condition && (
                    <>
                      <span className="text-gray-400">Condizione:</span>
                      <span className="text-white">{article.condition}</span>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Full description */}
        <Card className="mb-8 bg-[#12121a] border-gray-800">
          <CardContent className="pt-6">
            <h2 className="text-2xl font-bold text-white mb-4">Descrizione completa</h2>
            <div
              className="prose prose-invert max-w-none text-gray-300 whitespace-pre-wrap"
              data-testid="product-detail-description"
            >
              {article.description || "Nessuna descrizione disponibile."}
            </div>
          </CardContent>
        </Card>

        {/* Continua la Collezione */}
        {seriesData && seriesData.items && seriesData.items.length > 0 && (
          <Card className="border-2 border-amber-500/40 bg-[#12121a]" data-testid="continue-collection-section">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                  📚 Continua la Collezione
                </h2>
                <Badge className="bg-amber-200 text-amber-900 border-amber-300 text-sm">
                  Serie: {seriesData.series}
                </Badge>
              </div>
              {(() => {
                const availableIds = seriesData.items
                  .filter((it) => (it.quantity_stock || 0) > 0 || it.availability_type === "preorder")
                  .map((it) => it.id);
                const allSelected = availableIds.length > 0 && availableIds.every((id) => collectionPick[id]);
                if (availableIds.length < 2) return null;
                return (
                  <button
                    type="button"
                    onClick={() =>
                      setCollectionPick(allSelected ? {} : Object.fromEntries(availableIds.map((id) => [id, true])))
                    }
                    className="mb-3 text-sm font-bold text-amber-700 hover:text-amber-900 underline underline-offset-2"
                    data-testid="series-select-all"
                  >
                    {allSelected ? "✕ Deseleziona tutti" : `☑️ Seleziona tutti i ${availableIds.length} disponibili`}
                  </button>
                );
              })()}
              <div className="text-sm text-gray-300 mb-4 bg-amber-500/10 rounded-lg p-3 border border-amber-500/30">
                {(() => {
                  const selectedCount = Object.values(collectionPick).filter(Boolean).length;
                  if (selectedCount === 0) {
                    return (
                      <>
                        💡 <strong>Seleziona più numeri della serie</strong> e ricevili tutti in un unico pacco:
                        paghi <strong>una sola spedizione (€9)</strong> invece di una per ogni ordine separato.
                      </>
                    );
                  }
                  if (selectedCount === 1) {
                    return (
                      <>
                        💡 Aggiungine almeno <strong>1 ancora</strong> e risparmi <strong>€9</strong> di spedizione
                        per ogni numero in più (li ricevi tutti insieme in un solo pacco).
                      </>
                    );
                  }
                  const saving = (selectedCount - 1) * 9;
                  return (
                    <>
                      ✅ Con questi <strong>{selectedCount} numeri</strong> insieme paghi UNA spedizione di €9.
                      Risparmi <strong>€{saving.toFixed(2)}</strong> rispetto a {selectedCount} ordini separati!
                    </>
                  );
                })()}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
                {seriesData.items.map((it) => {
                  const checked = !!collectionPick[it.id];
                  const out = (it.quantity_stock || 0) <= 0 && it.availability_type !== "preorder";
                  return (
                    <label
                      key={it.id}
                      className={`block border-2 rounded-lg p-2 cursor-pointer transition-all ${
                        checked ? "border-amber-500 bg-amber-500/15 shadow-md" : "border-gray-800 bg-[#0d0d14] hover:border-amber-400"
                      } ${out ? "opacity-50 cursor-not-allowed" : ""}`}
                      data-testid={`series-item-${it.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={out}
                          onChange={(e) =>
                            setCollectionPick((prev) => ({ ...prev, [it.id]: e.target.checked }))
                          }
                          className="mt-1 h-4 w-4 text-amber-600 focus:ring-amber-500 rounded"
                        />
                        <div className="flex-1 min-w-0">
                          {it.image && (
                            <div className="w-full aspect-square bg-white rounded mb-1 overflow-hidden">
                              <img src={it.image} alt={it.title} className="w-full h-full object-contain" />
                            </div>
                          )}
                          <div className="text-xs font-semibold text-white line-clamp-2">
                            {it.series_number ? `n.${it.series_number} — ` : ""}
                            {it.title.replace(/^[^—-]*(?:n[\.\s]*\d+\s*)?/i, "").substring(0, 50)}
                          </div>
                          <div className="text-sm font-bold text-amber-700 mt-1">
                            € {Number(it.price || 0).toFixed(2)}
                          </div>
                          {out && (
                            <Badge variant="destructive" className="text-[10px] mt-1">Esaurito</Badge>
                          )}
                          {it.availability_type === "preorder" && (
                            <Badge className="text-[10px] mt-1 bg-orange-100 text-orange-800">🚀 Prevendita</Badge>
                          )}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
              <div className="flex items-center justify-between flex-wrap gap-3 pt-3 border-t border-amber-500/20">
                <div className="text-sm text-gray-300">
                  <strong>{Object.values(collectionPick).filter(Boolean).length}</strong> articoli selezionati ·{" "}
                  <strong>€{(Object.values(collectionPick).filter(Boolean).length === 0
                    ? 0
                    : seriesData.items
                        .filter((it) => collectionPick[it.id])
                        .reduce((s, it) => s + Number(it.price || 0), 0)
                  ).toFixed(2)}</strong> totale prodotti
                </div>
                <Button
                  onClick={addCollectionToCart}
                  disabled={collectionAdding || Object.values(collectionPick).filter(Boolean).length === 0}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="add-collection-to-cart-btn"
                >
                  {collectionAdding ? "Aggiungo..." : "🛒 Aggiungi selezionati al carrello"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Chi ha comprato questo, ha comprato anche... */}
        {(() => {
          // Exclude items already shown in "Continua la Collezione" to avoid duplication.
          const seriesIds = new Set((seriesData?.items || []).map((s) => s.id));
          const visible = fbtItems.filter((it) => !seriesIds.has(it.id));
          if (visible.length === 0) return null;
          const label =
            fbtBasedOn === "orders"
              ? "🛒 Chi ha comprato questo, ha comprato anche…"
              : "✨ Ti potrebbero interessare anche";
          return (
            <Card className="mt-8 border border-blue-500/30 bg-[#12121a]" data-testid="fbt-section">
              <CardContent className="pt-6">
                <h2 className="text-2xl font-bold text-white mb-1 flex items-center gap-2">
                  {label}
                </h2>
                <p className="text-sm text-gray-400 mb-5">
                  {fbtBasedOn === "orders"
                    ? "Suggerimenti basati sui veri ordini dei nostri clienti."
                    : "Selezionati per te tra i più amati della categoria."}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                  {visible.slice(0, 6).map((it) => {
                    const out = (it.quantity_stock || 0) <= 0 && it.availability_type !== "preorder";
                    const totalPrice =
                      Number(it.price || 0) *
                      (1 + (Number(it.iva_percentage) || 0) / 100);
                    // Backend may return relative /api/image/... paths for legacy
                    // articles stored on local disk — prepend BACKEND_URL so the
                    // <img> tag resolves even on alternate domains.
                    const imgSrc = it.image
                      ? it.image.startsWith("http")
                        ? it.image
                        : `${BACKEND_URL}${it.image}`
                      : null;
                    return (
                      <Link
                        key={it.id}
                        to={`/prodotto/${it.slug || it.id}`}
                        className="group block bg-[#0d0d14] rounded-lg border border-gray-800 hover:border-blue-400 hover:shadow-md transition-all overflow-hidden"
                        data-testid={`fbt-item-${it.id}`}
                      >
                        <div className="aspect-square bg-white overflow-hidden flex items-center justify-center">
                          {imgSrc ? (
                            <img
                              src={imgSrc}
                              alt={it.title}
                              loading="lazy"
                              className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform"
                            />
                          ) : (
                            <Package className="w-10 h-10 text-gray-300" />
                          )}
                        </div>
                        <div className="p-2">
                          <div className="text-xs font-semibold text-white line-clamp-2 min-h-[2.4em]">
                            {it.title}
                          </div>
                          <div className="flex items-center justify-between mt-1.5">
                            <span className="text-sm font-bold text-blue-400">
                              €{totalPrice.toFixed(2)}
                            </span>
                            {out ? (
                              <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                                Esaurito
                              </Badge>
                            ) : it.availability_type === "preorder" ? (
                              <Badge className="text-[10px] px-1.5 py-0 bg-orange-100 text-orange-800">
                                🚀
                              </Badge>
                            ) : (
                              <Badge className="text-[10px] px-1.5 py-0 bg-green-100 text-green-800">
                                ✓
                              </Badge>
                            )}
                          </div>
                          {!out && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                quickAddToCart(it);
                              }}
                              className="mt-2 w-full bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-bold py-1.5 rounded-md transition-colors"
                              data-testid={`fbt-quick-add-${it.id}`}
                            >
                              ➕ Aggiungi
                            </button>
                          )}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Reviews */}
        <Card className="bg-[#12121a] border-gray-800" id="recensioni-prodotto">
          <CardContent className="pt-6">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" /> Recensioni
            </h2>
            <ReviewSection
              articleId={article.id}
              reviews={reviews}
              showReviewForm={showReviewForm}
              reviewForm={reviewForm}
              setReviewForm={setReviewForm}
              onToggleForm={(id) => setShowReviewForm((prev) => ({ ...prev, [id]: !prev[id] }))}
              onSubmit={submitReview}
            />
          </CardContent>
        </Card>

        {/* Related products */}
        {related.length > 0 && (
          <section className="mt-12" data-testid="related-products-section">
            <h2 className="text-2xl font-bold text-white mb-6">
              Altri prodotti in <span className="text-pink-500">{article.category}</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {related.map((a) => (
                <ProductCard
                  key={a.id}
                  article={a}
                  discount={findArticleDiscount(activePromotions, a.id)}
                  badges={null}
                  wishlistCount={0}
                  wishlistIds={wishlistIds}
                  isFocused={false}
                  onZoomImage={() => {}}
                  onAddToCart={() => {
                    const cart = JSON.parse(localStorage.getItem("cart") || "[]");
                    const idx = cart.findIndex((i) => i.article_id === a.id);
                    if (idx >= 0) cart[idx].quantity += 1;
                    else cart.push({ article_id: a.id, quantity: 1, article: a });
                    localStorage.setItem("cart", JSON.stringify(cart));
                    setCartCount(cart.reduce((sum, it) => sum + (it.quantity || 0), 0));
                    window.dispatchEvent(new Event("cart:updated"));
                    showToast(`🛒 ${a.title} aggiunto al carrello!`);
                  }}
                  onToggleWishlist={() => toggleWishlist()}
                  reviews={{}}
                  showReviewForm={{}}
                  reviewForm={reviewForm}
                  setReviewForm={() => {}}
                  onToggleReviewForm={() => {}}
                  onSubmitReview={() => {}}
                />
              ))}
            </div>
          </section>
        )}
      </div>

      <Lightbox lightbox={lightbox} onClose={() => setLightbox(null)} />

      <ShareDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        url={`https://edicolavillaricca.com/prodotto/${article.slug || article.id}`}
        shareUrl={(() => {
          // Inject the logged-in customer's referral_code so the share link
          // credits them if the recipient buys. Falls back to plain link for
          // anonymous visitors.
          let ref = "";
          try {
            const stored = JSON.parse(localStorage.getItem("user") || "null");
            ref = stored?.referral_code || "";
          } catch (_) { /* ignore */ }
          const base = `${BACKEND_URL}/api/og/prodotto/${article.slug || article.id}`;
          return ref ? `${base}?ref=${encodeURIComponent(ref)}` : base;
        })()}
        title={article.title}
        description={article.description || ""}
        image={images[0]}
        price={formatPriceWithIva(article.price, article.iva_percentage)}
        onToast={(m) => showToast(m)}
      />

      {/* Sticky mobile add-to-cart bar — always visible while scrolling on mobile
          so the user never has to scroll back up to buy. Hidden on desktop. */}
      <div
        className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-[#0d0d14] border-t border-gray-800 shadow-[0_-4px_16px_rgba(0,0,0,0.5)] px-3 py-2 flex items-center gap-2 pb-[max(env(safe-area-inset-bottom),0.5rem)]"
        data-testid="mobile-sticky-cta"
      >
        <div className="flex-1 min-w-0">
          <div className="text-[10px] text-gray-500 uppercase tracking-wider leading-none">
            {discount ? "Sconto attivo" : "Prezzo"}
          </div>
          <div className="text-lg font-extrabold leading-tight truncate">
            {discount ? (
              <>
                <span className="text-red-500">
                  {formatPrice(getDiscountedPrice(article.price, article.iva_percentage, discount))}
                </span>
                <span className="text-xs text-gray-400 line-through ml-1">
                  {formatPriceWithIva(article.price, article.iva_percentage)}
                </span>
              </>
            ) : (
              <span className="text-green-700">
                {formatPriceWithIva(article.price, article.iva_percentage)}
              </span>
            )}
          </div>
        </div>
        {article.quantity_stock > 0 ? (
          <Button
            onClick={addToCart}
            className="bg-green-600 hover:bg-green-700 text-white font-bold rounded-full px-5 py-6 flex-shrink-0 whitespace-nowrap"
            data-testid="mobile-sticky-add-to-cart"
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            Aggiungi
          </Button>
        ) : (
          <Button disabled className="bg-gray-400 text-white rounded-full px-5 py-6 flex-shrink-0">
            Esaurito
          </Button>
        )}
      </div>
      {/* Spacer to prevent the sticky bar from covering page content bottom */}
      <div className="h-20 md:hidden" aria-hidden="true" />
    </div>
  );
};

export default ProductDetailPage;
